/**
 * Il vocabolario della riga del tabellone: come si chiamano gli stati, di che
 * colore sono, quando una riga è fuori gioco.
 *
 * Estratto da `CarichiPrevistiView` quando il calendario è diventato la seconda
 * vista sulle stesse righe (#39). Se «Sollecitare» si chiamasse in due modi nei
 * due posti, sarebbero due tabelloni — ed è esattamente ciò che stiamo evitando.
 */

import { cn } from "@/lib/utils";
import type { CaricoPrevistoRow, StatoCaricoPrevisto } from "@/types/api";

/**
 * La ricerca su una riga del tabellone: cliente, NDP, DDT, commesse, riferimenti del
 * cliente, note. Sta qui perché la usano in due — la tabella e il calendario — e
 * devono cercare **sugli stessi campi**: due ricerche che trovano cose diverse
 * sullo stesso dato sono peggio di una ricerca sola.
 *
 * I riferimenti cliente sono la ragione per cui esiste: cercare «26IT0992» o
 * «PO 18» trova righe che col solo nome del cliente non si troverebbero.
 *
 * Le **note** sono l'unico campo libero della riga: è lì che Marina scrive quello
 * che il tabellone non ha una colonna per dire (un vettore, un magazzino, un
 * vincolo del cliente), e senza cercarci dentro quelle righe si ritrovano solo
 * scorrendo.
 */
export function cercaRighe(items: CaricoPrevistoRow[], q: string): CaricoPrevistoRow[] {
  const t = q.trim().toLowerCase();
  if (!t) return items;
  // Le targhe si scrivono in mille modi — «FE 826 VA», «fe826va», «FE-826-VA»:
  // si confrontano senza spazi né trattini (01-10-2026).
  const tCompatta = compatta(t);
  return items.filter(
    (r) =>
      [
        r.cliente_nome,
        r.ndp_numero ?? "",
        r.ddt_numero ?? "",
        r.commesse.join(" "),
        r.riferimenti_cliente.join(" "),
        // il numero d'ordine del cliente, dall'export (30-09-2026)
        (r.commesse_info ?? []).map((c) => c.riferimento_cliente ?? "").join(" "),
        r.trasportatore ?? "",
        r.note ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(t) ||
      (tCompatta.length >= 3 &&
        [r.targa_motrice, r.targa_rimorchio].some((x) => x && compatta(x).includes(tCompatta))),
  );
}

function compatta(s: string): string {
  return s.replace(/[^a-z0-9]/gi, "").toLowerCase();
}

/** Le targhe della riga come si leggono in tabella: «FE826VA · rim. GS933FN». */
export function testoTarghe(r: Pick<CaricoPrevistoRow, "targa_motrice" | "targa_rimorchio">): string | null {
  const parti = [r.targa_motrice, r.targa_rimorchio ? `rim. ${r.targa_rimorchio}` : null].filter(
    (x): x is string => !!x,
  );
  return parti.length ? parti.join(" · ") : null;
}

export const STATO_LABEL: Record<StatoCaricoPrevisto, string> = {
  in_attesa: "In attesa",
  confermato: "Confermato",
  posticipato: "Posticipato",
  interlocutoria: "Interlocutoria",
  sollecito_dovuto: "Sollecitare",
  ndp_emessa: "NDP emessa",
  allineato_ddt: "Allineato DDT",
  partito: "Partito",
};

export const STATO_CLASSE: Record<StatoCaricoPrevisto, string> = {
  in_attesa: "bg-blue-100 text-blue-700",
  confermato: "bg-emerald-100 text-emerald-700",
  posticipato: "bg-amber-100 text-amber-700",
  interlocutoria: "bg-amber-100 text-amber-700",
  sollecito_dovuto: "bg-red-100 text-red-700",
  ndp_emessa: "bg-slate-100 text-slate-600",
  allineato_ddt: "bg-slate-100 text-slate-600",
  partito: "bg-emerald-100 text-emerald-800",
};

/** La riga non è più in gioco: la merce è uscita. */
export function eUscita(stato: StatoCaricoPrevisto): boolean {
  return stato === "partito";
}

/** Il tempo cliente si congela alla NDP, non alla partenza. */
export function eCongelato(stato: StatoCaricoPrevisto): boolean {
  return stato === "ndp_emessa" || stato === "allineato_ddt" || stato === "partito";
}

export const NDP_LABEL: Record<string, string> = {
  emessa: "Emessa",
  non_emessa: "Non emessa",
  non_condivisa: "Non condivisa",
};

/**
 * Il DDT è emesso ma la riga è ancora sul tabellone: è un DDT **di oggi**, il
 * camion sta caricando o è appena uscito (06-10-2026). Domani la riga parte da
 * sola alla data del DDT; Marina può confermarla prima col camion.
 */
export function eInPartenza(r: Pick<CaricoPrevistoRow, "ddt_numero" | "stato">): boolean {
  return !!r.ddt_numero && r.stato !== "partito";
}

export function StatoBadge({
  stato,
  inPartenza = false,
}: {
  stato: StatoCaricoPrevisto;
  inPartenza?: boolean;
}) {
  if (inPartenza) {
    return (
      <span
        className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
        title="DDT emesso oggi: il carico è in partenza. Domani esce dal tabellone da solo."
      >
        DDT emesso
      </span>
    );
  }
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", STATO_CLASSE[stato])}>
      {STATO_LABEL[stato]}
    </span>
  );
}
